import { useProductPageStore } from '../../contexts/ProductPageStore'
import ReviewCard from '../reviews/reviewCard'
import ReviewImages from '../reviews/reviewImages'
import styles from './reviewSection.module.scss'

import React, { useState } from 'react'

import ErrorBoundary from 'Clutch/UtilityComponents/ErrorBoundary'
import classNames from 'classnames'

import { createClickedSegmentEvent } from 'Clutch/Utilities/Instrumentation/Impressions/impressionSegmentEvents'
import LayerColorFinder from 'Clutch/Utilities/LayerColorFinder'

import Accordion from 'Clutch/Molecules/Accordion'
import Button from 'Clutch/Molecules/Button'

import Card from 'Clutch/Atoms/Card'
import Divider from 'Clutch/Atoms/Divider'
import Select from 'Clutch/Atoms/Form/Elements/Select'
import Meter from 'Clutch/Atoms/Meter'
import Panel from 'Clutch/Atoms/Panel'
import Rating from 'Clutch/Atoms/Rating'
import Svg from 'Clutch/Atoms/Svg'
import TextLink from 'Clutch/Atoms/TextLink'
import Typography from 'Clutch/Atoms/Typography'

const ReviewSection = () => {
  const isRatingsAndReviewsAccordionOpen = useProductPageStore((x) => x.isRatingsAndReviewsAccordionOpen)

  return (
    <Card fill className={styles.rating}>
      <Accordion
        intent={'action'}
        iconClosed={'plus'}
        iconOpened={'minus'}
        size={0.875}
        summary={<ReviewSummary />}
        fill
        segmentAccordionName={'pdp ratings'}
        defaultOpen={isRatingsAndReviewsAccordionOpen}
        alignIconToTop={true}
      >
        <div className={styles.accordionBody}>
          <NormalReviewGuts />
        </div>
      </Accordion>
    </Card>
  )
}

const NormalReviewGuts = ({}) => {
  const reviewData = useProductPageStore((x) => x.powerReviewsResponse)
  const backgroundColor = LayerColorFinder(1, null, null, null)
  const focusColor = LayerColorFinder(3, null, null, null)
  const skuBaseNumber = useProductPageStore((x) => x.skuBaseNumber)
  const powerReviewsMerchantId = useProductPageStore((x) => x.powerReviewsMerchantId)
  const powerReviewsMerchantGroupId = useProductPageStore((x) => x.powerReviewsMerchantGroupId)
  const powerReviewsApiKey = useProductPageStore((x) => x.powerReviewsApiKey)
  const [isFiltered, setIsFiltered] = useState(false)

  if (!reviewData) return false

  if ((!reviewData.results || !reviewData.results?.[0]?.reviews?.length) && !isFiltered) {
    return (
      <div className={styles.noReviews}>
        <TextLink
          intent={'action'}
          targetBlank
          segmentEvent={createClickedSegmentEvent('WriteAReview')}
          href={`/ReviewSubmission?pr_page_id=${skuBaseNumber}&pr_merchant_id=${powerReviewsMerchantId}&pr_merchant_group_id=${powerReviewsMerchantGroupId}`}
        >
          Be the first to write a review.
        </TextLink>
      </div>
    )
  }

  return (
    <div style={{ '--bg-color': backgroundColor, '--focus-color': focusColor }}>
      <div className={styles.reviewButtonWrapper}>
        <Button
          className={styles.readallreviews_btn}
          brand={'secondary'}
          segmentEvent={createClickedSegmentEvent('WriteAReview')}
          size={'small'}
          text={'Write A Review'}
          href={`/ReviewSubmission?pr_page_id=${skuBaseNumber}&pr_merchant_id=${powerReviewsMerchantId}&pr_merchant_group_id=${powerReviewsMerchantGroupId}`}
        />
      </div>
      <ReviewImages />
      <Rollup rollupData={reviewData.results?.[0].rollup} />
      <FilterSortAndSearch reviewCount={reviewData.paging?.total_results} setIsFiltered={setIsFiltered} />
      {reviewData.results &&
        reviewData.results.map((page, pageNum) => {
          return (page.reviews || []).map((review, i) => {
            return (
              <ReviewCard
                reviewData={review}
                key={`review_card_${i}_page_${pageNum}`}
                powerReviewsApiKey={powerReviewsApiKey}
                powerReviewsMerchantId={powerReviewsMerchantId}
              />
            )
          })
        })}
      <LoadMoreButton paging={reviewData.paging} />
    </div>
  )
}

const RatingSummary = ({ children }) => {
  const rating = useProductPageStore((x) => x.rating)
  const reviewCount = useProductPageStore((x) => x.reviewCount)

  return (
    <ErrorBoundary>
      <Typography as={'h2'} size={1.25} font={'heading'}>
        {'Ratings & Reviews'}
      </Typography>
      {reviewCount > 0 && (
        <ErrorBoundary>
          <Typography size={0.875} tone={'subtle'} inline>
            {`${rating} `}
          </Typography>
          <Svg icon={'star'} brand={'secondary'} size={0.875} />
          <Typography size={0.875} tone={'subtle'} inline className={styles.reviews}>
            {` ${reviewCount} ${reviewCount === 1 ? 'Review' : 'Reviews'}`}
          </Typography>
        </ErrorBoundary>
      )}
      {children}
    </ErrorBoundary>
  )
}

const ReviewSummary = () => {
  const summarizedReview = useProductPageStore((x) => x.summarizedReview)

  return (
    <>
      <RatingSummary />
      {summarizedReview && (
        <div className={styles.summarizedReview}>
          <div className={styles.ratingSummaryHeader}>
            <Typography as={'h3'} size={1.25}>
              Overall Assessment
            </Typography>
          </div>
          <Typography data-testid={'summarized-review'}>{summarizedReview}</Typography>
        </div>
      )}
    </>
  )
}

const Rollup = ({ rollupData }) => {
  if (!rollupData || !rollupData.review_count) return false

  return (
    <>
      <Card fill layer={1}>
        <div className={styles.rollupHeader}>
          <Typography size={2} font={'bold'}>
            {rollupData.average_rating}
          </Typography>
          <div>
            <Rating ratingValue={rollupData.average_rating} size={1} />
            <Typography tone={'subtle'} size={0.875}>
              {rollupData.review_count} reviews
            </Typography>
          </div>
        </div>
        <div className={styles.gutters}>
          {(rollupData.rating_histogram ?? []).map((value, index) => {
            return (
              <div key={`rollup_rating_${5 - index}`} className={styles.inlineGrid}>
                <Typography size={0.75} font={'bold'} className={styles.leftMargin}>
                  {5 - index}
                </Typography>
                <Svg size={0.875} icon={'star'} brand={'secondary'} />
                <Meter brand={'secondary'} value={value} max={rollupData.review_count} />
                <Typography size={0.75} tone={'subtle'}>
                  {value}
                </Typography>
              </div>
            )
          })}
        </div>
      </Card>
      <div className={styles.faceoffs}>
        <ReviewCard condensed titleOverride={'Most Liked Positive Review'} reviewData={rollupData.faceoff_positive} />
        <ReviewCard condensed titleOverride={'Most Liked Negative Review'} reviewData={rollupData.faceoff_negative} />
      </div>
    </>
  )
}

const LoadMoreButton = ({ paging }) => {
  const loadMoreReviews = useProductPageStore((x) => x.loadMoreReviews)
  const powerReviewsApiKey = useProductPageStore((x) => x.powerReviewsApiKey)

  if (!paging) return false

  let reviewsShown = paging.page_size * paging.current_page_number
  if (reviewsShown > paging.total_results) {
    reviewsShown = paging.total_results
  }

  let nextPageUrl = null
  if (paging.next_page_url) {
    nextPageUrl = `${paging.next_page_url}&apikey=${powerReviewsApiKey}`
  }

  return (
    <>
      {nextPageUrl && (
        <div className={styles.buttonWrapper}>
          <Button
            className={styles.readallreviews_btn}
            intent={'subdued'}
            segmentEvent={createClickedSegmentEvent('LoadMoreReviews')}
            type={'div'}
            size={'small'}
            text={'Load More Reviews'}
            onClick={() => loadMoreReviews(nextPageUrl)}
          />
        </div>
      )}
      <Typography size={0.875} tone={'subtle'} className={styles.loadMoreText}>
        Showing {reviewsShown} of {paging.total_results} reviews
      </Typography>
    </>
  )
}

const FilterSortAndSearch = ({ setIsFiltered, reviewCount }) => {
  const [selectedFilter, setSelectedFilter] = useState(0)
  const [sortValue, setSortValue] = useState('Newest')

  const sortOptions = [
    { label: 'Most Recent', value: 'Newest' },
    { label: 'Most Helpful', value: 'MostHelpful' },
    { label: 'Lowest Rated', value: 'LowestRating' },
    { label: 'Highest Rated', value: 'HighestRating' },
    { label: 'Oldest', value: 'Oldest' },
    { label: 'Images', value: 'MediaSort' },
  ]

  const loadFilteredReviews = useProductPageStore((x) => x.loadFilteredReviews)

  return (
    <div>
      <Divider className={styles.marginTop} />
      <Filter
        loadFilteredReviews={loadFilteredReviews}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        sortValue={sortValue}
        setIsFiltered={setIsFiltered}
      />
      <Sort
        loadFilteredReviews={loadFilteredReviews}
        selectedFilter={selectedFilter}
        sortValue={sortValue}
        setSortValue={setSortValue}
        sortOptions={sortOptions}
      />
      {/* <Search reviewCount={reviewCount}/> */}
    </div>
  )
}

const Filter = ({ loadFilteredReviews, selectedFilter, setSelectedFilter, sortValue, setIsFiltered }) => {
  return (
    <div className={styles.marginTop}>
      <Typography size={0.875} font={'bold'}>
        Filter by Ratings
      </Typography>
      <div className={styles.filterPills}>
        <Button
          id={'review-filter-0'}
          className={classNames(styles.filterPill, selectedFilter === 0 ? styles.bgColorSubdued : styles.bgColorNormal)}
          intent={'subdued'}
          segmentEvent={createClickedSegmentEvent('ReviewFilter')}
          size={'small'}
          onClick={() => {
            setSelectedFilter(0)
            setIsFiltered(false)
            loadFilteredReviews(0, sortValue)
          }}
        >
          <Typography>All Reviews</Typography>
        </Button>
        {[1, 2, 3, 4, 5].map((rating) => (
          <Button
            key={`review-filter-${rating}`}
            id={`review-filter-${rating}`}
            className={classNames(styles.filterPill, selectedFilter === rating ? styles.bgColorSubdued : '')}
            intent={'subdued'}
            segmentEvent={createClickedSegmentEvent('ReviewFilter')}
            size={'small'}
            onClick={() => {
              setSelectedFilter(rating)
              setIsFiltered(true)
              loadFilteredReviews(rating, sortValue)
            }}
          >
            <Typography>{`${rating} Star${rating > 1 ? 's' : ''}`}</Typography>
          </Button>
        ))}
      </div>
    </div>
  )
}

const Sort = ({ loadFilteredReviews, selectedFilter, sortValue, setSortValue, sortOptions }) => {
  return (
    <Panel layer={0} className={styles.reviewSort}>
      <Select
        id={'review_sort'}
        testingName={'review_sort'}
        className={styles.reviewSortSelect}
        layer={1}
        displayLabel={'false'}
        value={sortValue}
        options={sortOptions}
        borderIntent={'subdued'}
        label={'Sort Reviews'}
        onChange={(e) => {
          setSortValue(e.target.value)
          loadFilteredReviews(selectedFilter, e.target.value)
        }}
      />
    </Panel>
  )
}

// const Search = ({ reviewCount }) => {
//   const [isLoading, setIsLoading] = useState(false)

//   const handleSearch = async (e) => {
//     e.preventDefault()
//     setIsLoading(true)
//     setIsLoading(false)
//   }

//   return (
//     <form onSubmit={handleSearch} className={styles.form}>
//       <input
//         title={"reviewSearch"}
//         id="reviewSearchBar"
//         placeholder={"Search Reviews"}
//         className={classNames(
//           styles.mySearchBar,
//           styles.reviewSearchBar,
//         )}
//         type="text"
//       />
//     </form>
//   )
// }

export default ReviewSection
